/**
 * Comandos do chat do escritório: tudo o que começa por "/".
 *
 * Um comando nunca sai do navegador de quem o escreve — nem balão, nem
 * histórico, nem servidor. Quem o escreveu vê o comando e a resposta no chat
 * (marcados "só tu vês"); os outros não vêem nada.
 */
import { NOME_DO_TEMPO, TEMPOS, type Tempo, tempoAgora } from "./officeClima";

export type AcaoDoComando = { tipo: "clima"; tempo: Tempo | null };

export interface ResultadoDoComando {
  /** O que o chat responde, só a quem escreveu. */
  resposta: string;
  acao?: AcaoDoComando;
}

/** Outras maneiras de dizer cada tempo. */
const SINONIMOS: Record<string, Tempo | "auto"> = {
  limpo: "limpo",
  sol: "limpo",
  ensolarado: "limpo",
  chuva: "chuva",
  chover: "chuva",
  tempestade: "tempestade",
  trovoada: "tempestade",
  raios: "tempestade",
  neve: "neve",
  nevar: "neve",
  nevoeiro: "nevoeiro",
  nevoa: "nevoeiro",
  névoa: "nevoeiro",
  neblina: "nevoeiro",
  vento: "ventania",
  ventania: "ventania",
  folhas: "ventania",
  auto: "auto",
  automatico: "auto",
  automático: "auto",
  normal: "auto",
};

const LISTA = [...TEMPOS, "auto"].join(", ");

const AJUDA = `Comandos (só tu vês): /clima <${LISTA}> muda o tempo no teu ecrã · /clima mostra o actual · /ajuda`;

/**
 * Interpreta uma frase do chat. `null` se não for comando (começa por "/");
 * se for, a resposta a mostrar e, se houver, o que fazer.
 */
export function interpretarComando(texto: string): ResultadoDoComando | null {
  const limpo = texto.trim();
  if (!limpo.startsWith("/")) return null;
  const [nome = "", ...resto] = limpo.slice(1).split(/\s+/);
  const comando = nome.toLowerCase();
  const argumento = resto.join(" ").trim().toLowerCase();

  if (comando === "ajuda" || comando === "help" || comando === "comandos") return { resposta: AJUDA };

  if (comando === "clima" || comando === "tempo") {
    if (!argumento) {
      return {
        resposta: `Agora: ${NOME_DO_TEMPO[tempoAgora()]}. Para mudar, /clima e um destes: ${LISTA} ("auto" volta ao de todos).`,
      };
    }
    const escolha = SINONIMOS[argumento];
    if (!escolha) return { resposta: `Não conheço o tempo "${argumento}". Experimenta: ${LISTA}.` };
    if (escolha === "auto") {
      return {
        resposta: "Clima de volta ao automático — o mesmo que toda a gente vê.",
        acao: { tipo: "clima", tempo: null },
      };
    }
    return {
      resposta: `Clima mudado para "${NOME_DO_TEMPO[escolha]}" — só no teu ecrã. /clima auto para voltar.`,
      acao: { tipo: "clima", tempo: escolha },
    };
  }

  return { resposta: `Comando desconhecido: /${nome}. Escreve /ajuda para ver os comandos.` };
}
